import { Section } from '@/components/Section';
import { ActionButton } from '@/components/shared/button/ActionButton';
import { useParams } from 'react-router-dom';
import { FormCustomField } from './FormCustomField';
import { Button, Dialog } from 'alurkerja-ui';
import { FormContainer } from '@/components/form/FormContainer';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  customFieldSchema,
  CustomFieldService,
  ICustomFieldSchema,
} from '@/api/customField';
import { ProjectService } from '@/api/project';
import { Loading } from '@/components/Loading';
import { useState } from 'react';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { FaArrowLeft } from 'react-icons/fa';
import { ModuleType } from '@/utils/constants/customField';
import { useBackNavigate } from '@/utils/hooks/useBackNavigate';

export const CreateEditCustomField: React.FC = () => {
  const backNavigate = useBackNavigate();
  const { id } = useParams();

  const [isNext, setIsNext] = useState<boolean>(false);

  const {
    data: customFieldData,
    isFetching: isFetchingCustomFieldData,
    refetch,
  } = CustomFieldService.useGet(id);

  const form = useForm<ICustomFieldSchema>({
    resolver: zodResolver(customFieldSchema),
    values: customFieldData,
  });

  const { mutate: create, isPending: isCreateLoading } =
    CustomFieldService.useCreate();
  const { mutate: update, isPending: isUpdateLoading } =
    CustomFieldService.useUpdate();

  const projectIds =
    customFieldData?.project_custom_field?.map(item => item.project_id) || [];
  const customFieldType = customFieldData?.custom_field_type || '';
  const isMandatoryData = customFieldData?.is_mandatory;
  const isSearchableData = customFieldData?.is_searchable;
  const customOptionsData = customFieldData?.custom_options || [];

  const onSubmit = (data: ICustomFieldSchema) => {
    if (data.module_type === ModuleType.TEST_RUN) {
      data.is_mandatory = true;
      data.is_searchable = true;
      data.custom_field_type = 'CHECK_BOXES';
    } else {
      data.is_mandatory = data.is_mandatory[0] === 'true';
      data.is_searchable = data.is_searchable === 'true';
    }

    const errors: string[] = [];
    if (!data.options || data.options[0]?.option === '') {
      errors.push('Custom option is required');
    }
    if (!data.label_field) {
      errors.push('Label field is required');
    }
    if (!data.project_ids || data.project_ids.length === 0) {
      errors.push('Project is required');
    }
    if (errors.length > 0) {
      Dialog.error({
        title: 'Error',
        description: errors.join('\n'),
      });
      return;
    }

    const custom_option = data.options?.map(option => option.option);
    const { ...rest } = data;
    const mergedData = { ...rest, custom_option: custom_option };

    if (id) {
      update(mergedData, {
        onSuccess: () => {
          Dialog.success({
            title: 'Success',
            description: 'Custom field has been updated',
            callback: () => {
              refetch();
              backNavigate();
            },
          });
        },
      });
    } else {
      create(mergedData, {
        onSuccess: () => {
          Dialog.success({
            title: 'Success',
            description: 'Custom field has been created',
            callback: backNavigate,
          });
        },
        onError: () => {
          Dialog.error({
            title: 'Error',
            description: 'Failed to create test case',
          });
        },
      });
    }
  };

  const { data: projectData, isFetching: isFetchLoading } =
    ProjectService.useGetProjectOption();

  if (isFetchLoading || isFetchingCustomFieldData) {
    return (
      <Section>
        <Loading />
      </Section>
    );
  }

  return (
    <Section
      title={id ? 'Edit Custom Field' : 'Add Custom Field'}
      actions={
        <div className="flex flex-row gap-2">
          <ActionButton.Close onClick={backNavigate}></ActionButton.Close>
        </div>
      }
    >
      <div className="mx-5">
        <FormContainer form={form} onSubmit={onSubmit}>
          {!isNext ? (
            <div className="flex justify-end">
              <Button type="button" onClick={() => setIsNext(true)}>
                Next
              </Button>
            </div>
          ) : (
            <div className="flex justify-between">
              <OutlineButton
                icon={<FaArrowLeft />}
                onClick={() => setIsNext(false)}
              >
                Back
              </OutlineButton>
              <Button loading={isCreateLoading || isUpdateLoading}>Save</Button>
            </div>
          )}
          <FormCustomField
            projectIdsData={projectIds}
            customFieldTypeData={customFieldType}
            projectData={projectData}
            isNext={isNext}
            isMandatoryData={isMandatoryData}
            isSearchableData={isSearchableData}
            customOptionsData={customOptionsData}
          ></FormCustomField>
        </FormContainer>
      </div>
    </Section>
  );
};
